import React, { useContext, useEffect, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import { Button, Alert, Spinner } from 'react-bootstrap';
import { supabase } from '../supabase/supabase';
import { GlobalContext } from '../context/GlobalContext';

interface Plan {
    id: string;
    name: string;
    price: number;
    duration: number;
    description?: string;
}

interface StripeFormProps {
    missionaryId: string;
    onClose: () => void;
    onSuccess?: () => void;
}

// Initialize Stripe outside the component
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY || '');

export const StripeForm: React.FC<StripeFormProps> = ({ missionaryId, onClose, onSuccess }) => {
    const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
    const [clientSecret, setClientSecret] = useState<string | null>(null);
    const [plans, setPlans] = useState<Plan[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const global = useContext(GlobalContext);
    const userEmail = global?.user?.email;

    useEffect(() => {
        async function fetchPlans() {
            try {
                setLoading(true);
                const { data, error } = await supabase.from('plans').select('id, name, price, duration, description');
                if (error) throw error;
                setPlans(data || []);
            } catch (err: any) {
                setError('Failed to load plans');
                console.error('Error fetching plans:', err);
            } finally {
                setLoading(false);
            }
        }

        fetchPlans();
    }, []);

    const handleCheckoutComplete = async () => {
        try {
            const { data, error } = await supabase.functions.invoke('verify-checkout-session', {
                body: { clientSecret, missionaryId }
            });

            if (error) throw error;
            
            onSuccess?.();
            onClose();
        } catch (err: any) {
            setError('Payment verification failed');
            console.error('Error verifying checkout session:', err);
        }
    };

    const createCheckoutSession = async (planId: string) => {
        try {
            setLoading(true);
            setError(null);
            
            const { data, error } = await supabase.functions.invoke('create-checkout-session', {
                body: { 
                    planId, 
                    userEmail, 
                    missionaryId,
                    successUrl: `${window.location.origin}/dashboard`,
                    cancelUrl: `${window.location.origin}/dashboard`
                }
            });

            if (error) throw error;
            
            setClientSecret(data.clientSecret);
        } catch (err: any) {
            setError('Failed to create checkout session');
            console.error('Error creating checkout session:', err);
        } finally {
            setLoading(false);
        }
    };

    const handlePlanSelect = async (plan: Plan) => {
        setSelectedPlan(plan);
        await createCheckoutSession(plan.id);
    };

    if (loading && plans.length === 0) {
        return (
            <div className="stripe-loading">
                <Spinner animation="border" variant="primary" />
                <p>Loading plans...</p>
            </div>
        );
    }

    // Temporary placeholder - billing integration not completed
    const BILLING_INTEGRATION_COMPLETE = false;

    if (!BILLING_INTEGRATION_COMPLETE) {
        return (
            <div className="stripe-form">
                <div className="billing-placeholder text-center py-5">
                    <div className="mb-4">
                        <div className="placeholder-icon mb-3" style={{ fontSize: '3rem', color: '#6c757d' }}>⚠️</div>
                        <h4 className="mb-3">Billing Integration Not Complete</h4>
                        <p className="text-muted mb-4">
                            The payment system is currently under development. 
                            Please check back soon to subscribe to a plan.
                        </p>
                        <Button variant="outline-secondary" onClick={onClose}>
                            Close
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="stripe-form">
            {error && (
                <Alert variant="danger" className="mb-3">
                    {error}
                </Alert>
            )}

            {!selectedPlan ? (
                <div className="plans-selection">
                    <div className="plans-grid">
                        {plans.map((plan) => (
                            <div key={plan.id} className="plan-option">
                                <div className="plan-header">
                                    <h4 className="plan-name">{plan.name}</h4>
                                    <div className="plan-price">
                                        <span className="price-amount">${plan.price}</span>
                                        <span className="price-period">/{plan.duration} month{plan.duration > 1 ? 's' : ''}</span>
                                    </div>
                                </div>
                                {plan.description && (
                                    <p className="plan-description">{plan.description}</p>
                                )}
                                <Button
                                    onClick={() => handlePlanSelect(plan)}
                                    disabled={loading}
                                    className="select-plan-btn"
                                    size="lg"
                                >
                                    {loading ? 'Loading...' : 'Select Plan'}
                                </Button>
                            </div>
                        ))}
                    </div>
                </div>
            ) : clientSecret ? (
                <div className="checkout-container">
                    <EmbeddedCheckoutProvider
                        stripe={stripePromise}
                        options={{ clientSecret, onComplete: handleCheckoutComplete }}
                    >
                        <EmbeddedCheckout />
                    </EmbeddedCheckoutProvider>
                </div>
            ) : (
                <div className="stripe-loading">
                    <Spinner animation="border" variant="primary" />
                    <p>Preparing checkout...</p>
                </div>
            )}
        </div>
    );
};
